import { add, eachDayOfInterval, getISODay, isAfter, isBefore } from "date-fns";
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";

import TimeSlot from "models/TimeSlot";
import { compareAbsoluteDate, compareAbsoluteUTCTime, compareDate } from "utils/dateUtils";

class PeriodConfig {
  static fromAPI = (data) => ({
    end: data.end,
    endDate: data.end_date,
    endTime: data.end_time ? data.end_time.slice(0, 5) : null,
    eventId: data.event_id,
    excludedDays: data.excluded_days,
    id: data.id,
    name: data.name,
    start: data.start,
    startDate: data.start_date,
    startTime: data.start_time ? data.start_time.slice(0, 5) : null,
    timezone: data.timezone,
    weekdays: data.weekdays,
  });

  /**
   * This method generate the timeslot describes by the given period config
   * @param {Object} periodConfig The period config that describes the timeslots to generate.
   * @param {boolean} shouldReturnUnexpiredOnly Whether we want to return only the timeslot in the present and future.
   * @return {[{start: Date, end: Date}]} The list of timeslots generated by the config.
   */
  static periodConfigToTimeSlots = (periodConfig, shouldReturnUnexpiredOnly = false) => {
    const now = new Date(Date.now());
    const nowUTC = new Date(
      Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours(), now.getMinutes()),
    );
    const orgaTz = periodConfig.timezone;
    const orgaNow = utcToZonedTime(nowUTC, orgaTz);

    // We just return an elementary timeslot according to the periodConfig start and end fields
    if (periodConfig.start && periodConfig.end) {
      if (!shouldReturnUnexpiredOnly || Date.parse(periodConfig.end) >= now.getTime()) {
        return [
          {
            start: new Date(Date.parse(periodConfig.start)),
            end: new Date(Date.parse(periodConfig.end)),
          },
        ];
      }
      return [];
    }

    // We generate a timeslot according to the periodConfig
    if (periodConfig.startTime && periodConfig.endTime && periodConfig.startDate && periodConfig.endDate) {
      const periodConfigStartDate = new Date(Date.parse(periodConfig.startDate));
      const periodConfigStartTimeSplit = periodConfig.startTime.split(":");
      const periodConfigStartTime = new Date(2020, 1, 1, 0, 0, 0);
      periodConfigStartTime.setUTCHours(periodConfigStartTimeSplit[0]);
      periodConfigStartTime.setUTCMinutes(periodConfigStartTimeSplit[1]);

      const periodConfigEndDate = new Date(Date.parse(periodConfig.endDate));
      const periodConfigEndTimeSplit = periodConfig.endTime.split(":");
      const periodConfigEndTime = new Date(2020, 1, 1, 0, 0, 0);
      periodConfigEndTime.setUTCHours(periodConfigEndTimeSplit[0]);
      periodConfigEndTime.setUTCMinutes(periodConfigEndTimeSplit[1]);

      const periodConfigExcludedDays = (
        periodConfig.excludedDays
        && periodConfig.excludedDays.length
        && periodConfig.excludedDays.map((day) => new Date(Date.parse(day)))
      );

      const timeSlotOverTwoDays = compareAbsoluteUTCTime(periodConfigStartTime, periodConfigEndTime, ">=");

      // eslint-disable-next-line no-nested-ternary
      const startDate = shouldReturnUnexpiredOnly && compareAbsoluteDate(periodConfigStartDate, orgaNow, "<=")
        // eslint-disable-next-line no-nested-ternary
        ? timeSlotOverTwoDays
          ? compareAbsoluteUTCTime(orgaNow, periodConfigEndTime, "<")
            ? add(new Date(Date.parse(orgaNow.toISOString().split("T")[0])), { days: -1 })
            : new Date(Date.parse(orgaNow.toISOString().split("T")[0]))
          : compareAbsoluteUTCTime(orgaNow, periodConfigEndTime, "<")
            ? new Date(Date.parse(orgaNow.toISOString().split("T")[0]))
            : add(new Date(Date.parse(orgaNow.toISOString().split("T")[0])), { days: 1 })
        : periodConfigStartDate;
      const endDate = periodConfigEndDate;

      if (compareAbsoluteDate(startDate, endDate, "<=")) {
        return eachDayOfInterval({ start: startDate, end: endDate })
          .map((day) => new Date(Date.parse(day)))
          .filter((day) => (
            (
              !periodConfig.weekdays
              || !periodConfig.weekdays.length
              || periodConfig.weekdays.includes(getISODay(day) - 1)
            )
            && (
              !periodConfigExcludedDays
              || !periodConfigExcludedDays.length
              || !periodConfigExcludedDays.some((excludedDay) => compareAbsoluteDate(excludedDay, day, "=="))
            )
          ))
          .map((day) => ({
            start: zonedTimeToUtc(
              // eslint-disable-next-line max-len
              new Date(day.getFullYear(), day.getMonth(), day.getDate(), periodConfigStartTimeSplit[0], periodConfigStartTimeSplit[1], 0),
              orgaTz,
            ),
            end: add(
              zonedTimeToUtc(
                // eslint-disable-next-line max-len
                new Date(day.getFullYear(), day.getMonth(), day.getDate(), periodConfigEndTimeSplit[0], periodConfigEndTimeSplit[1], 0),
                orgaTz,
              ),
              { days: timeSlotOverTwoDays ? 1 : 0 },
            ),
          }));
      }
      return [];
    }

    return [];
  };

  /**
   * This method generate the timeslot describes by the given list of period configs
   * @param {[{Object}]} periodConfigs The list of period configs that describe the timeslots to generate.
   * @param {boolean} shouldReturnUnexpiredOnly Whether we want to return only the timeslot in the present and future.
   * @return {[{start: Date, end: Date}]} The list of timeslots generated by the configs.
   */
  static periodConfigsToTimeSlots = (periodConfigs, shouldReturnUnexpiredOnly = false) => {
    let timeSlots = [];
    periodConfigs
      ?.forEach((pc) => {
        timeSlots = timeSlots.concat(PeriodConfig.periodConfigToTimeSlots(pc, shouldReturnUnexpiredOnly));
      });
    return TimeSlot.mergeTimeSlots(timeSlots);
  };

  /**
   * This method compare two periodConfigs and check whether they are overlapping each other.
   * @param {Object} periodConfigA The first period config to compare.
   * @param {Object} periodConfigB The second period config to compare.
   * @return {boolean} Whether the to given period configs are overlapping each other.
   */
  static arePeriodConfigsOverlapping = (periodConfigA, periodConfigB) => (
    compareDate(new Date(Date.parse(periodConfigA.startDate)), new Date(Date.parse(periodConfigB.endDate)), ">=")
    || compareDate(new Date(Date.parse(periodConfigB.startDate)), new Date(Date.parse(periodConfigA.endDate)), ">=")
  );

  /**
   * TODO: Optimize this function to avoid to compute all timeslots.
   * This method check whether the period config describe a time slot that match the current date.
   * @param periodConfigs The list of period configs to check.
   */
  static arePeriodConfigsActive = (periodConfigs) => {
    const now = new Date(Date.now());

    const unexpiredTimeSlots = PeriodConfig.periodConfigsToTimeSlots(periodConfigs, true);
    const startDate = !!unexpiredTimeSlots.length && unexpiredTimeSlots[0].start;
    const endDate = !!unexpiredTimeSlots.length && unexpiredTimeSlots[0].end;

    return startDate && endDate && isAfter(now, startDate) && isBefore(now, endDate);
  };

  /**
   * TODO: Optimize this function to avoid to compute all timeslots.
   * This method check whether the period config describe no more timeslot that ends after now.
   * @param periodConfigs The list of period configs to check.
   */
  static arePeriodConfigsExpired = (periodConfigs) => {
    const now = new Date(Date.now());

    const unexpiredTimeSlots = PeriodConfig.periodConfigsToTimeSlots(periodConfigs, false);
    if (!unexpiredTimeSlots?.length) {
      return null;
    }

    const indexLastTimeSlot = unexpiredTimeSlots?.length - 1;
    const endDate = unexpiredTimeSlots[indexLastTimeSlot].end;

    return endDate && isBefore(endDate, now);
  };
}

export default PeriodConfig;
